import React, { useState } from 'react';
import { Input, notification } from 'antd';
import { useQueryClient } from 'react-query';
import Modal from '../../../components/Modal';
import { useUpdateTitle } from '../../../core/modules/book/hooks/useUpdateTitle.query';
import {
  AUTHOR_TAGS,
  AUTHORS,
  PODIUMAUTHORS,
  SEND_TO_CRM,
  SENT_TO_CRM_RR,
  SOURCE,
  WATCHLIST
} from '../../../core/constants';
import { IBook } from '../../../section/Common/types/types';
import showNotification from '../../../section/Common/utils/showNotification';
import { useActions } from '../../../context/Actions.context';

const { TextArea } = Input;

interface IProps {
  book: IBook | Record<string, string | number>;
  modal: boolean;
  toggleModal: Function;
  id: string;
}

const watchlistObject = (source, id, book) => {
  if (source === SOURCE.bookstat) {
    return {
      obj: { amazonasin: book.ASIN, notes: '' },
      obj_type: 'Title'
    };
  } else if (source === SOURCE['royal-road']) {
    return (id === AUTHORS || id === PODIUMAUTHORS)
      ? { obj: { author_id: book['Author Id'], tag: 'watchlist', notes: '' }, obj_type: 'Author' }
      : { obj: { fiction_id: book['Fiction Id'], notes: '' }, obj_type: 'Title' };
  }
};

const WatchlistTitles: React.FC<IProps> = ({ book, modal, toggleModal, id }) => {
  const { source, isActionLoading, setActionLoading } = useActions();
  const queryClient = useQueryClient();
  const watchlist = watchlistObject(source['source'], id, book);
  const objectType = watchlist?.obj_type ?? 'Title';
  const titleForNotification = typeof book?.Title === 'string' ? book.Title : undefined;
  const [notes, setNotes] = useState<string>('');

  const { mutateAsync: mutateWatchlist } = useUpdateTitle(WATCHLIST);
  const { mutateAsync: mutateAuthorWatchlist } = useUpdateTitle(AUTHOR_TAGS);

  const handleClose = () => {
    setNotes('');
    toggleModal();
  };

  const onSend = () => {
    setActionLoading(true);

    const body = {
      ...(notes.trim() && { notes: notes.trim() })
    };

    const promise = (id === AUTHORS || id === PODIUMAUTHORS)
      ? mutateAuthorWatchlist({ params: { ...watchlist?.obj }, body })
      : mutateWatchlist({ params: { ...watchlist?.obj }, body });

    showNotification(
      promise,
      `The ${objectType} was successfully added to watchlist.`,
      `The ${objectType} has not been sent to watchlist.`,
      titleForNotification
    );

    promise.then(() => {
      if (id === SEND_TO_CRM || id === SENT_TO_CRM_RR) {
        notification.info({
          description: 'Please delete the data from Hubspot.',
          message: ''
        });
      }
    });
    promise.then(() => queryClient.refetchQueries(`get-row-count${id}`));
    promise.then(() => queryClient.refetchQueries(`get-${id}`));
    promise.finally(() => setActionLoading(false));

    handleClose();
  };

  return (
    <Modal
      okText={'Send To Watchlist'}
      title='Send To Watchlist'
      visible={modal}
      onOk={onSend}
      confirmLoading={isActionLoading}
      onCancel={handleClose}
    >
      <div>
        <span>Help yourself and others understand why you’re sending this. Leave a note, or skip and continue.</span>
        <br />
        <br />
        <TextArea
          placeholder='Add your notes here...'
          maxLength={400}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={4}
        />

        <div style={{ textAlign: 'right', fontSize: 12, marginTop: 4 }}>
          {notes.length} / 400
        </div>
      </div>
    </Modal>
  );
};

export default React.memo(WatchlistTitles);
